/**
 * Eric 2026-05-12 — Utilitaires partagés pour le traitement de transcripts.
 * Extraits de VideoDetailPage.js dans le cadre du refacto P2.
 */

/**
 * Parse un transcript Fireflies/AssemblyAI au format DOCX importé en texte plein
 * (sans segments structurés). Reconnaît plusieurs formats d'export Fireflies
 * courants pour produire des blocs 3-cols. Renvoie [{start, speaker, text}].
 *
 * Formats supportés :
 *   A) MM:SS\nLocuteur\nTexte                                   (legacy)
 *   B) Locuteur MM:SS\nTexte                                    (Fireflies docx commun)
 *   C) [MM:SS] Locuteur: Texte                                  (variante crochets inline)
 *   D) Locuteur (MM:SS)\nTexte                                  (variante paren)
 *   E) MM:SS - Locuteur: Texte                                  (tiret)
 *   F) Paragraphes sans timecode séparés par lignes vides       (fallback ultime)
 */
const _toSeconds = (h, m, s) => {
  const hh = parseInt(h || "0", 10) || 0;
  const mm = parseInt(m || "0", 10) || 0;
  const ss = parseInt(s || "0", 10) || 0;
  return hh * 3600 + mm * 60 + ss;
};

export function parseTextTranscriptToSegments(fullText) {
  if (!fullText || typeof fullText !== "string") return [];
  const text = fullText.replace(/\r\n/g, "\n");

  // ── Format A : timecode → locuteur → texte (3 lignes)
  const reA = /^\[?(\d{1,2}):(\d{2})(?::(\d{2}))?\]?[ \t]*\n([^\n]+)\n([\s\S]*?)(?=\n\s*\[?\d{1,2}:\d{2}|$(?![\s\S]))/gm;
  const segsA = [];
  let m;
  while ((m = reA.exec(text)) !== null) {
    const start = m[3] ? _toSeconds(m[1], m[2], m[3]) : _toSeconds(0, m[1], m[2]);
    const speaker = (m[4] || "").trim();
    const txt = (m[5] || "").trim().replace(/\s+/g, " ");
    if (txt) segsA.push({ start, speaker, text: txt });
  }
  if (segsA.length > 0) return segsA;

  // ── Format B : "Locuteur MM:SS" en début de ligne, suivi du texte
  // Exemple Fireflies : "Eric Roy 0:15\nBonjour à tous, ..."
  const reB = /^([A-ZÀ-Ý][\wÀ-ÿ.'\- ]{0,60}?)[ \t]+\[?(\d{1,2}):(\d{2})(?::(\d{2}))?\]?[ \t]*\n([\s\S]*?)(?=\n[A-ZÀ-Ý][\wÀ-ÿ.'\- ]{0,60}?[ \t]+\[?\d{1,2}:\d{2}|$(?![\s\S]))/gm;
  const segsB = [];
  while ((m = reB.exec(text)) !== null) {
    const speaker = m[1].trim();
    const start = m[4] ? _toSeconds(m[2], m[3], m[4]) : _toSeconds(0, m[2], m[3]);
    const txt = (m[5] || "").trim().replace(/\s+/g, " ");
    if (txt) segsB.push({ start, speaker, text: txt });
  }
  if (segsB.length > 0) return segsB;

  // ── Format C : "[MM:SS] Locuteur: texte" tout sur une ligne (split par changement de ligne ou par marker)
  const reC = /\[(\d{1,2}):(\d{2})(?::(\d{2}))?\][ \t]+([^:\n]{1,60}?)[ \t]*:[ \t]*([^\n]+)/g;
  const segsC = [];
  while ((m = reC.exec(text)) !== null) {
    const start = m[3] ? _toSeconds(m[1], m[2], m[3]) : _toSeconds(0, m[1], m[2]);
    const speaker = m[4].trim();
    const txt = m[5].trim();
    if (txt) segsC.push({ start, speaker, text: txt });
  }
  if (segsC.length > 0) return segsC;

  // ── Format D : "Locuteur (MM:SS)" → texte (paren timecode)
  const reD = /^([A-ZÀ-Ý][\wÀ-ÿ.'\- ]{0,60}?)[ \t]*\((\d{1,2}):(\d{2})(?::(\d{2}))?\)[ \t]*\n([\s\S]*?)(?=\n[A-ZÀ-Ý][\wÀ-ÿ.'\- ]{0,60}?[ \t]*\(\d{1,2}:\d{2}|$(?![\s\S]))/gm;
  const segsD = [];
  while ((m = reD.exec(text)) !== null) {
    const speaker = m[1].trim();
    const start = m[4] ? _toSeconds(m[2], m[3], m[4]) : _toSeconds(0, m[2], m[3]);
    const txt = (m[5] || "").trim().replace(/\s+/g, " ");
    if (txt) segsD.push({ start, speaker, text: txt });
  }
  if (segsD.length > 0) return segsD;

  // ── Format E : "MM:SS - Locuteur: texte"
  const reE = /^(\d{1,2}):(\d{2})(?::(\d{2}))?[ \t]*[-–][ \t]*([^:\n]{1,60}?):[ \t]*([^\n]+)/gm;
  const segsE = [];
  while ((m = reE.exec(text)) !== null) {
    const start = m[3] ? _toSeconds(m[1], m[2], m[3]) : _toSeconds(0, m[1], m[2]);
    const speaker = m[4].trim();
    const txt = m[5].trim();
    if (txt) segsE.push({ start, speaker, text: txt });
  }
  if (segsE.length > 0) return segsE;

  // ── Format F : aucun timecode reconnu — découpe par paragraphes (lignes vides),
  // affecte timecode=0 et speaker="—". Permet quand même l'affichage 3-cols
  // au lieu d'un blob "font-serif" laid.
  const paragraphs = text.split(/\n\s*\n+/).map(p => p.trim()).filter(Boolean);
  if (paragraphs.length > 1) {
    return paragraphs.map(p => ({ start: 0, speaker: "—", text: p.replace(/\s+/g, " ") }));
  }

  // Vraiment rien → retourne vide (le rendu UI affichera un message clair).
  return [];
}
